import { ErrorHandler, NgModule, Injectable } from '@angular/core';
import { HTTP_INTERCEPTORS, HttpErrorResponse } from '@angular/common/http';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app.routing';
import { AuthHttpService } from './services/authentication/auth-http.service';
import { DataService } from './services/data.service';
import { AuthGuardService } from './core/services/auth-guard/auth-guard.service';
import { EventTrackerService } from './services/event-tracker.service';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { NZ_I18N, en_US } from 'ng-zorro-antd/i18n';   // Locale for AntD datepicker
import * as Sentry from '@sentry/browser';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ContentfulService } from './services/contentful.service';
import { ContentfulCacheService } from './services/contentful-cache.service';
import { AppPreloadingStrategy } from './core/app.preload';
import { HttpErrorLoggingInterceptor } from './core/interceptors/http-error-logger.interceptor';
import { NgcCookieConsentModule, NgcCookieConsentConfig } from 'ngx-cookieconsent';
import { version as appVersion } from '../../package.json';
import { FarmsService } from './services/farms.service';
import { registerLocaleData } from '@angular/common';
import en from '@angular/common/locales/en';
import { AlertDevEnvComponent } from './core/components/alert-dev-env/alert-dev-env.component';
import { NAVY_BASE } from './app.constants';
import { AppCommonModule } from './app.common.module';

registerLocaleData(en);

const cookieConfig: NgcCookieConsentConfig = {
  cookie: {
    domain: window.location.hostname !== 'localhost' ? '.aerobotics.com' : 'localhost'
  },
  position: 'bottom',
  theme: 'classic',
  palette: {
    popup: {
      background: '#FFF',
      text: '#000',
      link: '#000'
    },
    button: {
      background: NAVY_BASE,
      text: '#FFF',
      border: 'transparent'
    }
  },
  type: 'info',
  content: {
    message: 'We use cookies to personalize and enhance your experience on our site. By using our site, you agree to our use of cookies',
    dismiss: 'Accept',
    deny: 'Refuse cookies',
    link: 'Learn more',
    href: 'https://support.aerobotics.com/category/58-terms-and-policies',
    policy: 'Cookie Policy'
  }
};


const SENTRY_CONFIG = {
  dsn: 'https://d61a166900a847a3a9cfe8d9dcd2787d@o101176.ingest.sentry.io/1399197',
  // dsn: 'https://657347f8988144d48cc3da2915f82e56@sentry.io/257986',
  release: appVersion
};

class PotentialHandledHttpError extends Error {
  public name;

  constructor(error: HttpErrorResponse) {
    const errorUrl = error.url ? error.url : '';
    const errorStatus = error.status ? error.status : '';
    const message = `${errorUrl} ${errorStatus} ${JSON.stringify(error.error)}`;
    super(message);
    this.name = 'PotentialUnhandledHttpError';
    Object.setPrototypeOf(this, new.target.prototype);
  }

}

if (window.location.hostname !== 'localhost' && window.location.hostname !== 'dev.aerobotics.com') {
  Sentry.init(SENTRY_CONFIG);
}

@Injectable()
export class SentryErrorHandler extends ErrorHandler {
  handleError(error: any): void {
    // Only capture the exception if the hostname is not localhost or dev.aerobotics.com
    if (window.location.hostname !== 'localhost' && window.location.hostname !== 'dev.aerobotics.com') {
      if (error instanceof HttpErrorResponse) {
        // Suspect an uncaught HTTP error
        const potentialUncaughtHttpError = new PotentialHandledHttpError(error);
        Sentry.captureException(potentialUncaughtHttpError);
      } else {
        Sentry.captureException(error);
      }
    }
    console.error(error);
  }
}

@NgModule({
  declarations: [
    AppComponent,
    AlertDevEnvComponent
  ],
  imports: [
    BrowserModule,
    NgcCookieConsentModule.forRoot(cookieConfig),
    BrowserAnimationsModule,
    AppCommonModule,
    AppRoutingModule
  ],
  providers: [
    DataService,
    FarmsService,
    ContentfulService,
    ContentfulCacheService,
    AuthGuardService,
    AuthHttpService,
    EventTrackerService,
    AppPreloadingStrategy,
    {provide: MAT_DATE_LOCALE, useValue: 'en-GB'},
    {provide: NZ_I18N, useValue: en_US},
    {provide: ErrorHandler, useClass: SentryErrorHandler},
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorLoggingInterceptor,
      multi: true,
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor() { }
}
